import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase';
import Sidebanner from './sidebanner';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import FullPageLoader from '../../components/FullPageLoader';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

export default function Analytics() {
  const { business, loading: authLoading, updateWidgetSettings } = useAuth();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [stats, setStats] = useState({ 
    authorized: 0, 
    guests: 0, 
    invited: 0,
    authPct: 0,
    invitedPct: 0 
  });

  const [isEditing, setIsEditing] = useState(false);
  const [config, setConfig] = useState({ color: '#1C6065', darkMode: false, shadow: true });

  useEffect(() => {
    if (business?.www) {
      fetchAnalytics();
      setConfig({
        color: business.widget_color || '#1C6065',
        darkMode: business.is_dark_mode || false,
        shadow: business.has_shadow ?? true
      });
    }
  }, [business]);

  const handleUpdate = async () => {
    const result = await updateWidgetSettings(config);
    if (result.success) setIsEditing(false);
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { data: manifests, error } = await supabase
        .from('reviews')
        .select('created_at, user_id, status')
        .eq('company', business.www)
        .eq('is_approved', true)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const total = manifests?.length || 0;
      const authCount = manifests.filter(m => m.user_id !== null).length;
      const guestCount = total - authCount;
      const invitedCount = manifests.filter(m => m.status === 'invited').length;

      const authPct = total > 0 ? ((authCount / total) * 100).toFixed(0) : 0;
      const invitedPct = total > 0 ? ((invitedCount / total) * 100).toFixed(0) : 0;

      setStats({ authorized: authCount, guests: guestCount, invited: invitedCount, authPct, invitedPct });

      const last30Days = [...Array(30)].map((_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (29 - i));
        return { 
          date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), 
          count: 0 
        };
      });

      manifests.forEach(m => {
        const dateStr = new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const dayBucket = last30Days.find(d => d.date === dateStr);
        if (dayBucket) dayBucket.count++;
      });
      
      setChartData(last30Days);
    } catch (err) {
      console.error("Analytics Error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) return <FullPageLoader />;

  return (
    <div className='min-h-screen bg-[#F9FAFB] lg:pr-[250px] xl:pr-[350px] xl:pt-[100px] xs:pt-[80px] pt-[60px] pb-20'>
      <Helmet>
  <title>Analytics | Sitemanifest</title> 
  
  <meta name="robots" content="noindex, nofollow" />
  
  <meta name="googlebot" content="noarchive" />
</Helmet>
      <div className='max-w-[1000px] mx-auto px-6 font-poppins relative'>
        
        {/* WIDGET MODAL */}
        {isEditing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-md p-4">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
              <h3 className="text-xl font-bold mb-6">Widget Configuration</h3>
              <div className="space-y-6">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase">Brand Color</label>
                  <input type="color" className="w-full h-12 rounded-xl mt-2 border-none cursor-pointer" value={config.color} onChange={(e) => setConfig({...config, color: e.target.value})} />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                  <span className="text-sm font-semibold">Dark Mode</span>
                  <input type="checkbox" checked={config.darkMode} onChange={(e) => setConfig({...config, darkMode: e.target.checked})} className="w-5 h-5 accent-[#1C6065]" />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setIsEditing(false)} className="flex-1 py-4 text-gray-400 font-bold">Cancel</button>
                  <button onClick={handleUpdate} className="flex-1 py-4 bg-[#1C6065] text-white rounded-xl font-bold">Save Changes</button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='mb-10 mt-[25px]'>
          <h1 className='font-semibold text-2xl text-black uppercase tracking-tight'>Intelligence Dashboard</h1>
          <p className='text-gray-500 text-sm'>Reputation Analysis for {business?.title}</p>
        </div>

        {/* SECTION 1: PERCENTAGE MIRRORS */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-10'>
          <div className='bg-white p-8 rounded-3xl shadow-sm border border-gray-100'>
            <div className='flex justify-between items-end mb-4'>
              <div>
                <p className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Authorization Integrity</p>
                <h2 className='text-3xl font-bold text-black'>{stats.authPct}%</h2>
              </div>
              <p className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Auth / Guest</p>
            </div>
            <div className='w-full bg-gray-100 h-2.5 rounded-full overflow-hidden flex'>
              <div style={{ width: `${stats.authPct}%` }} className='bg-[#1C6065] h-full transition-all duration-1000' />
            </div>
            <p className='text-[11px] text-gray-400 mt-4 leading-relaxed'>
              {stats.authorized} Verified Manifests. {stats.guests} Guest entries.
            </p>
          </div>

          <div className='bg-white p-8 rounded-3xl shadow-sm border border-gray-100'>
            <div className='flex justify-between items-end mb-4'>
              <div>
                <p className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Invitation Conversion</p>
                <h2 className='text-3xl font-bold text-black'>{stats.invitedPct}%</h2>
              </div>
              <p className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>{stats.invited} Total</p>
            </div>
            <div className='w-full bg-gray-100 h-2.5 rounded-full overflow-hidden'>
              <div style={{ width: `${stats.invitedPct}%` }} className='bg-[#1C6065] h-full transition-all duration-1000' />
            </div>
            <p className='text-[11px] text-gray-400 mt-4 leading-relaxed'>Percentage of feedback generated through active invitations.</p>
          </div>
        </div>

        {/* SECTION 2: VOLUME CARDS */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-10'>
          <div className='bg-white p-6 rounded-2xl border border-gray-100'>
            <p className='text-gray-400 text-xs uppercase tracking-wider'>Total Volume</p>
            <h3 className='text-3xl font-bold mt-2 text-[#1C6065]'>{business?.reviews}</h3>
          </div>
          <div className='bg-white p-6 rounded-2xl border border-gray-100'>
            <p className='text-gray-400 text-xs uppercase tracking-wider'>Manifestscore</p>
            <h3 className='text-3xl font-bold mt-2 text-[#1C6065]'>{business?.trustscore?.toFixed(1) || '0.0'}</h3>
          </div>
          <div className='bg-white p-6 rounded-2xl border border-gray-100'>
            <p className='text-gray-400 text-xs uppercase tracking-wider'>Status</p>
            <h3 className={`text-2xl font-bold mt-2 ${business?.trustscore >= 4 ? 'text-green-600' : 'text-orange-500'}`}>
              {business?.trustscore >= 4 ? 'TOP TIER' : 'GROWING'}
            </h3>
          </div>
        </div>

        {/* SECTION 3: THE BETTER CHART UI */}
        <div className='bg-white p-8 rounded-3xl shadow-sm border border-gray-100 mb-10'>
          <h3 className='font-medium mb-6 text-gray-700'>Feedback Momentum (30 Days)</h3>
          <div className='h-[350px] w-full'>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={config.color} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={config.color} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
                <XAxis 
                  dataKey="date" 
                  tick={{fontSize: 10, fill: '#9CA3AF'}} 
                  axisLine={false} 
                  tickLine={false} 
                  interval={5} 
                  dy={10}
                />
                <YAxis hide />
                <Tooltip 
                   contentStyle={{ 
                     borderRadius: '16px', 
                     border: 'none', 
                     boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                     padding: '12px'
                   }}
                   cursor={{ stroke: config.color, strokeWidth: 2 }}
                />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stroke={config.color} 
                  fillOpacity={1} 
                  fill="url(#colorCount)" 
                  strokeWidth={4} 
                  animationDuration={1500}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* STRATEGIC MIRROR FOOTER */}
        <div className='bg-[#1C6065] text-white p-10 rounded-3xl flex flex-col md:flex-row items-center justify-between shadow-xl shadow-[#1C6065]/20'>
          <div>
            <h4 className='text-2xl font-semibold mb-2'>Strategic Mirror</h4>
            <p className='opacity-80 text-sm max-w-[450px] leading-relaxed'>
              Your identity trust is at <strong>{stats.authPct}%</strong>. Increasing this ratio directly correlates to higher conversion rates on your website widget.
            </p>
          </div>
          <button 
            onClick={() => navigate('/business/widget')} 
            className='bg-white text-[#1C6065] px-10 py-4 rounded-2xl font-bold text-sm hover:bg-gray-50 transition-all mt-8 md:mt-0 active:scale-95'
          >
            Widget Settings
          </button>
        </div>

      </div>
      <Sidebanner />
    </div>
  );
}